<template>
  <b-nav-item-dropdown
    class="dropdown-notification mr-25"
    menu-class="dropdown-menu-media"
    right
  >
    <template #button-content>
      <feather-icon
        :badge="unreadCount > 0 ? unreadCount.toString() : null"
        badge-classes="bg-danger"
        class="text-body"
        icon="BellIcon"
        size="21"
      />
    </template>

    <!-- Header -->
    <li class="dropdown-menu-header">
      <div class="dropdown-header d-flex">
        <h4 class="notification-title mb-0 mr-auto">
          Notificaciones
        </h4>
        <b-badge
          v-if="unreadCount > 0"
          pill
          variant="light-primary"
        >
          {{ unreadCount }} Nuevas
        </b-badge>
      </div>
    </li>

    <!-- Notifications -->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="scrollable-container media-list scroll-area"
      tagname="li"
    >
      <template v-if="notifications.length === 0">
        <div class="media d-flex align-items-center p-2">
          <small class="text-muted">No hay notificaciones</small>
        </div>
      </template>

      <b-link
        v-for="notification in notifications"
        :key="notification.id"
        class="media d-flex align-items-center"
        :class="{ 'notification-unread': !notification.read_at }"
        @click="goToOrder(notification)"
      >
        <b-media>
          <template #aside>
            <b-avatar
              size="32"
              :variant="getNotificationType(notification)"
            >
              <feather-icon icon="ShoppingCartIcon" />
            </b-avatar>
          </template>
          <p class="media-heading">
            <span class="font-weight-bolder">
              {{ getNotificationTitle(notification) }}
            </span>
          </p>
          <small class="notification-text">{{ getNotificationSubtitle(notification) }}</small>
          <small class="text-muted d-block mt-1">
            {{ formatDate(notification.created_at) }}
          </small>
        </b-media>
      </b-link>
    </vue-perfect-scrollbar>

    <!-- Footer -->
    <li
      v-if="notifications.length > 0"
      class="dropdown-menu-footer"
    >
      <b-button
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        variant="primary"
        block
        @click="markAllAsRead"
      >
        Marcar todas como leídas
      </b-button>
    </li>
  </b-nav-item-dropdown>
</template>

<script>
import {
  BNavItemDropdown, BBadge, BMedia, BLink, BAvatar, BButton,
} from 'bootstrap-vue'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import Ripple from 'vue-ripple-directive'
import axios from '@axios'

export default {
  components: {
    BNavItemDropdown,
    BBadge,
    BMedia,
    BLink,
    BAvatar,
    VuePerfectScrollbar,
    BButton,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      notifications: [],
      unreadCount: 0,
      processingNotification: null,
      perfectScrollbarSettings: {
        maxScrollbarLength: 60,
        wheelPropagation: false,
      },
    }
  },
  mounted() {
    this.loadNotifications()
    this.interval = setInterval(() => {
      this.loadNotifications()
    }, 30000)
  },
  beforeDestroy() {
    if (this.interval) {
      clearInterval(this.interval)
    }
  },
  methods: {
    loadNotifications() {
      axios
        .get('system/notifications')
        .then(response => {
          if (response.data.res) {
            this.notifications = response.data.data.notifications
            this.unreadCount = response.data.data.unread_count
          }
        })
        .catch(error => {
          console.error('Error al cargar las notificaciones:', error)
        })
    },
    markAsRead(notificationId) {
      return axios
        .put(`system/notifications/${notificationId}/read`)
        .then(() => {
          this.loadNotifications()
        })
        .catch(error => {
          console.error('Error al marcar como leída la notificación:', error)
        })
    },
    markAllAsRead() {
      axios
        .put('system/notifications/read-all')
        .then(() => {
          this.loadNotifications()
        })
        .catch(error => {
          console.error('Error al marcar como leída todas las notificaciones:', error)
        })
    },
    getNotificationTitle(notification) {
      const { data } = notification
      if (data.action === 'created') {
        return `Nuevo Pedido #${data.order_id}`
      } if (data.action === 'status_changed') {
        return `Pedido #${data.order_id} actualizado`
      } if (data.action === 'commented') {
        return `Nuevo comentario en el pedido #${data.order_id}`
      }
      return 'Notificación'
    },
    getNotificationSubtitle(notification) {
      const { data } = notification
      if (data.action === 'created') {
        return `Cliente: ${data.customer_name} - Total: $${parseFloat(data.status || 0).toFixed(2)}`
      } if (data.action === 'status_changed') {
        return `Cliente: ${data.customer_name} - Estado: ${data.status}`
      } if (data.action === 'commented') {
        return data.comment || 'Nuevo comentario'
      }
      return 'Sin detalles'
    },
    getNotificationType(notification) {
      const { data } = notification
      if (data.action === 'created') {
        return 'light-success'
      } if (data.action === 'status_changed') {
        return 'light-info'
      } if (data.action === 'commented') {
        return 'light-warning'
      }
      return 'light-primary'
    },
    goToOrder(notification) {
      if (this.processingNotification === notification.id) {
        return
      }
      const { data } = notification
      const orderId = data.order_id
      const currentRouteId = this.$route.id
      this.processingNotification = notification.id
      if (currentRouteId !== orderId.toString()) {
        this.$router.push({ name: 'sales-customer-orders', params: { id: orderId } })
          .catch(() => {})
      }

      this.markAsRead(notification.id).finally(() => {
        this.processingNotification = null
      })
    },
    formatDate(dateString) {
      const date = new Date(dateString)
      const now = new Date()
      const diff = now - date
      const minutes = Math.floor(diff / 60000)
      const hours = Math.floor(diff / 3600000)
      const days = Math.floor(diff / 86400000)

      if (minutes < 1) return 'Hace un momento'
      if (minutes < 60) return `Hace ${minutes} minuto${minutes > 1 ? 's' : ''}`
      if (hours < 24) return `Hace ${hours} hora${hours > 1 ? 's' : ''}`
      if (days < 7) return `Hace ${days} día${days > 1 ? 's' : ''}`

      return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })
    },
  },
}
</script>

<style scoped>
.notification-unread {
  background-color: #f8f9fa;
  font-weight: 500;
}
</style>
